import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  applyDocumentTheme,
  effectiveThemeMode,
  readStoredThemeMode,
  readThemePolicy,
  resolveTheme,
  subscribeSystemTheme,
  writeThemeMode,
  writeThemePolicy,
  type ResolvedTheme,
  type ThemeMode,
  type ThemePolicy,
} from "@app/shared/lib/theme/theme";

type ThemeContextType = {
  /** The mode in force: the student's pick, or the school's default (always it when switching is off). */
  mode: ThemeMode;
  /** What is on screen now. */
  resolved: ResolvedTheme;
  /** The school lets students pick light / dark / auto (portal design `theme.allowSwitch`). */
  allowSwitch: boolean;
  setMode: (mode: ThemeMode) => void;
  /** The school's default and switch permission; called once the school settings are known. */
  setPolicy: (policy: ThemePolicy) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  mode: "system",
  resolved: "light",
  allowSwitch: true,
  setMode: () => {},
  setPolicy: () => {},
});

/**
 * Sits above the school provider (the theme is painted before the school loads): it starts from the
 * policy cached by the previous visit and takes the live one through `setPolicy`.
 */
export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [stored, setStored] = useState<ThemeMode | null>(readStoredThemeMode);
  const [policy, setPolicyState] = useState<ThemePolicy>(readThemePolicy);
  const mode = effectiveThemeMode(stored, policy);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));

  useEffect(() => {
    const sync = () => setResolved(resolveTheme(mode));
    sync();
    return mode === "system" ? subscribeSystemTheme(sync) : undefined;
  }, [mode]);

  useEffect(() => applyDocumentTheme(resolved), [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    writeThemeMode(next);
    setStored(next);
  }, []);

  const setPolicy = useCallback((next: ThemePolicy) => {
    writeThemePolicy(next);
    setPolicyState((prev) => (prev.default === next.default && prev.allowSwitch === next.allowSwitch ? prev : next));
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, allowSwitch: policy.allowSwitch, setMode, setPolicy }),
    [mode, resolved, policy.allowSwitch, setMode, setPolicy],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
